
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface DataOneDebugViewProps {
  data: any;
  debugInfo: any;
}

export const DataOneDebugView: React.FC<DataOneDebugViewProps> = ({ data, debugInfo }) => {
  // Extract structure information from the response
  const topLevelKeys = data ? Object.keys(data) : [];
  
  // Analyze the query_responses structure
  let queryResponsesKeys: string[] = [];
  let firstResponseKeys: string[] = [];
  let styleDataPath: string = "Not found";
  let styleDataExists: boolean = false;
  
  if (data?.query_responses) {
    queryResponsesKeys = Object.keys(data.query_responses);
    
    if (queryResponsesKeys.length > 0) {
      const firstResponse = data.query_responses[queryResponsesKeys[0]];
      firstResponseKeys = Object.keys(firstResponse || {});
      
      // Check common paths for style data
      if (firstResponse?.styles?.style && Array.isArray(firstResponse.styles.style)) {
        styleDataPath = `query_responses.${queryResponsesKeys[0]}.styles.style[0]`;
        styleDataExists = true;
      } else if (firstResponse?.us_market_data?.us_styles && Array.isArray(firstResponse.us_market_data.us_styles)) {
        styleDataPath = `query_responses.${queryResponsesKeys[0]}.us_market_data.us_styles[0]`;
        styleDataExists = true;
      } else if (Array.isArray(firstResponse?.styles)) {
        styleDataPath = `query_responses.${queryResponsesKeys[0]}.styles[0]`;
        styleDataExists = true;
      }
    }
  }

  return (
    <Card className="p-0 w-full max-w-full">
      <CardHeader>
        <CardTitle>Response Structure Debug</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Data Structure Issue</AlertTitle>
            <AlertDescription>
              The DataOne response structure doesn't match what our parsers expect.
              This might be due to an API change or a different response format.
            </AlertDescription>
          </Alert>
          
          {debugInfo && (
            <div className="space-y-4">
              <h3 className="font-semibold">Debug Information</h3>
              <pre className="bg-gray-50 p-4 rounded-md text-xs overflow-auto">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}
          
          <div className="space-y-2">
            <h3 className="font-semibold">Response Structure Analysis</h3>
            
            <div className="space-y-2 p-4 bg-gray-50 rounded-md">
              <div>
                <span className="font-medium">Top-level keys:</span>
                <pre className="mt-1 text-xs overflow-auto">{JSON.stringify(topLevelKeys, null, 2)}</pre>
              </div>
              
              {data?.query_responses && (
                <>
                  <div className="mt-3">
                    <span className="font-medium">query_responses keys:</span>
                    <pre className="mt-1 text-xs overflow-auto">{JSON.stringify(queryResponsesKeys, null, 2)}</pre>
                  </div>
                  
                  {queryResponsesKeys.length > 0 && (
                    <div className="mt-3">
                      <span className="font-medium">First response keys:</span>
                      <pre className="mt-1 text-xs overflow-auto">{JSON.stringify(firstResponseKeys, null, 2)}</pre>
                    </div>
                  )}
                </>
              )}
              
              <div className="mt-3">
                <span className="font-medium">Style data path:</span> 
                <span className={styleDataExists ? "text-green-600" : "text-red-600"}> {styleDataPath}</span>
              </div>
            </div>
          </div>
          
          <Separator />
          
          <div className="space-y-2">
            <h3 className="font-semibold">Expected Structure</h3>
            <div className="text-sm space-y-1">
              <p>The DataOne parser expects this structure:</p>
              <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-auto">
{`{
  "query_responses": {
    "Request-Sample": {
      "vin": "...",
      "styles": {
        "style": [{ ... }]
      }
    }
  }
}`}
              </pre>
              <p className="mt-2">Or alternatively:</p>
              <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-auto">
{`{
  "query_responses": {
    "Request-Sample": {
      "vin": "...",
      "us_market_data": {
        "us_styles": [{ ... }]
      }
    }
  }
}`}
              </pre>
            </div>
          </div>
          
          <Separator />
          
          <div className="space-y-2">
            <h3 className="font-semibold">Response Preview (First Level)</h3>
            <div className="text-sm">
              <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-auto h-64">
                {JSON.stringify(data, null, 2).substring(0, 10000)}
              </pre>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
